import React, { useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  DetailContext,
  DetailHandle,
  FilesContext,
  FormCheckbox,
  FormFileField,
  FormLocalDateTimeField,
  FormPanel,
  FormSelect,
  FormTextField,
  TableFieldContext,
  useFormSelector,
} from '@eas/common-web';
import { useDeliveredDocumentPermission } from '@modules/document/document-permission';
import {
  DeliveredDocument,
  DictionaryAutocomplete,
  Document,
  DocumentAttachment,
} from '@models';
import { DocumentType, UIElement } from '@enums';
import { useAttachmentTypes } from './attachment-api';
import { useFiles } from './attachment-file-hook';
import { useDownloadValidationReportDialog } from './dialog-actions/download-validation-report-hook';

export function AttachmentDialog({
  documentId,
  documentType,
  disabled = false,
}: {
  documentId: string;
  documentType?: DocumentType;
  disabled?: boolean;
}) {
  const context = useContext(TableFieldContext);
  const { source } = useContext<DetailHandle<Document>>(DetailContext);

  const { context: filesContext } = useFiles(
    `/api/evidence-spisu/document/${documentId}/attachment/files`
  );

  const attachmentTypes = useAttachmentTypes(documentType, context?.disabled);
  const { hasPermission } = useDeliveredDocumentPermission(
    (source?.data as DeliveredDocument) ?? null
  );
  const { callApi: downloadReportApiCall, DownloadValidationReportButton } =
    useDownloadValidationReportDialog(false);

  const { id, file, validation, originFromEssl } = useFormSelector(
    (data: DocumentAttachment) => ({
      id: data.id,
      file: data.file,
      validation: data.validation,
      originFromEssl: data.originFromEssl,
    })
  );

  const downloadValidationReportDisabled =
    disabled ||
    documentType !== DocumentType.DELIVERED ||
    !id ||
    !validation?.pdfReportId ||
    !hasPermission(UIElement.Attachment.DOWNLOAD_VALIDATION_REPORT);

  const AttachmentFields = () => (
    <>
      <FormTextField
        name="name"
        label={
          <FormattedMessage
            id="ES__DOCUMENT__DETAIL__ATTACHMENT_FIELD_LABEL__NAME"
            defaultMessage="Název přílohy"
          />
        }
        autoFocus={true}
        disabled={disabled}
      />
      <FormSelect<DictionaryAutocomplete & { allowedFor: DocumentType[] }>
        name="type"
        label={
          <FormattedMessage
            id="ES__DOCUMENT__DETAIL__ATTACHMENT_FIELD_LABEL__TYPE"
            defaultMessage="Typ přílohy"
          />
        }
        source={attachmentTypes}
        tooltipMapper={(o) => o.name}
        valueIsId={true}
        disabled={disabled}
      />
      <FilesContext.Provider value={filesContext}>
        <FormFileField
          name="file"
          label={
            <FormattedMessage
              id="ES__DOCUMENT__DETAIL__ATTACHMENT_FIELD_LABEL__FILE"
              defaultMessage="Soubor"
            />
          }
          disabled={disabled || filesContext.loading || originFromEssl}
          before={filesContext.loading ? 'Nahrávání...' : undefined}
        />
      </FilesContext.Provider>
      {documentType &&
        file &&
        [DocumentType.OUTGOING, DocumentType.INTERNAL].includes(
          documentType
        ) && (
          <>
            <FormCheckbox
              name="convertToPdf"
              label={
                <FormattedMessage
                  id="ES__DOCUMENT__DETAIL__FIELD_LABEL__CONVERT_TO_PDF"
                  defaultMessage="Převést do PDF"
                />
              }
            />
            <FormCheckbox
              name="sign"
              label={
                <FormattedMessage
                  id="ES__DOCUMENT__DETAIL__FIELD_LABEL__SIGN"
                  defaultMessage="Opatřit el. pečetí"
                />
              }
            />
          </>
        )}
    </>
  );

  return (
    <>
      {(documentType !== DocumentType.DELIVERED || !validation) && (
        <AttachmentFields />
      )}
      {documentType === DocumentType.DELIVERED && validation && (
        <>
          <FormPanel label="Příloha">
            <AttachmentFields />
          </FormPanel>
          <FormPanel label="Validační report">
            <FormTextField
              name="validation.name"
              label="Název"
              disabled
              before={
                <DownloadValidationReportButton
                  disabled={downloadValidationReportDisabled}
                  apiCall={async () => await downloadReportApiCall(id!)}
                />
              }
            />
            <FormLocalDateTimeField
              name="validation.validationTime"
              label="Datum a čas validace"
              disabled
            />
          </FormPanel>
        </>
      )}
    </>
  );
}

export function AttachmentBulkCreateDialog() {
  return (
    <>
      <FormTextField
        name="name"
        label={
          <FormattedMessage
            id="ES__DELIVERED_DOCUMENT__DETAIL__ATTACHMENT_FIELD_LABEL__NAME"
            defaultMessage="Název přílohy"
          />
        }
        autoFocus={true}
      />
      <FormTextField
        name="count"
        type="number"
        label={
          <FormattedMessage
            id="ES__DELIVERED_DOCUMENT__DETAIL__ATTACHMENT_FIELD_LABEL__COUNT"
            defaultMessage="Počet příloh"
          />
        }
      />
    </>
  );
}
